import { useEffect, useMemo, useState } from "react";
import { PageHeader } from "../components/layout";
import { Button, Card, StatusIndicator } from "../components/ui";
import { ContextBar } from "../components/context-bar";
import {
  AdvancedJson,
  CountChip,
  Disclosure,
  KeyVal,
  SeverityBadge,
  StateTag,
  formatAge,
  shortHash,
} from "../components/phase3";
import { ApiUnavailableState, EmptyState, LoadingState, NotConfiguredState } from "../components/states";
import { useAppContext } from "../hooks/useAppContext";
import {
  api,
  apiConfigured,
  ApiError,
  type DiffOperation,
  type DryRunPlan,
  type DryRunView,
  type PersonaComparison,
  type PersonaResult,
  type SafetyFinding,
  type Severity,
} from "../lib/api";

export const NO_CHANGES_TEXT = "Dry run only. No Discord changes have been made.";

// ── Operation grouping ───────────────────────────────────────────────────────
type OpGroupKey = "roles" | "categories" | "channels" | "permissions" | "server-settings";
const OP_GROUP_LABEL: Record<OpGroupKey, string> = {
  roles: "Roles",
  categories: "Categories",
  channels: "Channels",
  permissions: "Permission overwrites",
  "server-settings": "Server settings",
};
const OP_GROUP_ORDER: OpGroupKey[] = ["roles", "categories", "channels", "permissions", "server-settings"];

function opGroup(op: DiffOperation): OpGroupKey {
  switch (op.resourceType) {
    case "role":
      return "roles";
    case "category":
      return "categories";
    case "channel":
      return "channels";
    case "overwrite":
      return "permissions";
    default:
      return "server-settings";
  }
}

function bucketCounts(counts: Record<string, number>): {
  create: number;
  update: number;
  move: number;
  preserve: number;
  conflict: number;
} {
  const sum = (pred: (t: string) => boolean) =>
    Object.entries(counts).reduce((acc, [t, n]) => (pred(t) ? acc + n : acc), 0);
  return {
    create: sum((t) => t.startsWith("create-")),
    update: sum((t) => t.startsWith("update-")),
    move: sum((t) => t === "move-channel" || t.startsWith("reorder-")),
    preserve: sum((t) => t.startsWith("preserve-") || t === "skip-managed-role"),
    conflict: sum((t) => t === "conflict"),
  };
}

const SEV_ORDER: Severity[] = ["BLOCKER", "ERROR", "WARNING", "INFO"];
const SEV_LABEL: Record<Severity, string> = {
  BLOCKER: "Blockers",
  ERROR: "Errors",
  WARNING: "Warnings",
  INFO: "Info",
};

// ── Presentational review (testable with mock data, no data fetching) ─────────
export function DryRunReview({
  plan,
  blueprintLabel,
  versionLabel,
  snapshotRequestedAt,
  generatedAt,
  now = Date.now(),
}: {
  plan: DryRunPlan;
  blueprintLabel?: string;
  versionLabel?: string;
  snapshotRequestedAt?: string | null;
  generatedAt?: string | null;
  now?: number;
}): JSX.Element {
  const buckets = bucketCounts(plan.summary?.operationCounts ?? {});
  const findingCounts = plan.summary?.findingCounts ?? { info: 0, warning: 0, error: 0, blocker: 0, blocks: false };
  const blocked = plan.status === "BLOCKED" || findingCounts.blocks;

  const groups = useMemo(() => {
    const out = new Map<OpGroupKey, DiffOperation[]>();
    for (const op of plan.operations ?? []) {
      const g = opGroup(op);
      if (!out.has(g)) out.set(g, []);
      out.get(g)!.push(op);
    }
    return out;
  }, [plan.operations]);

  const findingsBySeverity = useMemo(() => {
    const out = new Map<Severity, SafetyFinding[]>();
    for (const f of plan.findings ?? []) {
      if (!out.has(f.severity)) out.set(f.severity, []);
      out.get(f.severity)!.push(f);
    }
    return out;
  }, [plan.findings]);

  // Channel id → name maps for persona visibility diffs.
  const channelNames = useMemo(() => {
    const m = new Map<string, string>();
    const collect = (results: PersonaResult[] | undefined) => {
      for (const p of results ?? []) for (const c of p.channels ?? []) m.set(c.channelId, c.name);
    };
    collect(plan.personaSimulation?.current);
    collect(plan.personaSimulation?.desired);
    return m;
  }, [plan.personaSimulation]);
  const chName = (id: string) => channelNames.get(id) ?? id;

  return (
    <div className="dry-run">
      {/* The single most important, non-negotiable message on this page. */}
      <div className="dryrun-banner" role="status" aria-live="polite">
        <strong>{NO_CHANGES_TEXT}</strong>
        <span>
          This is a read-only preview generated by ODIN. There is no deploy action on this page — nothing was queued,
          approved, or applied to Discord.
        </span>
      </div>

      {/* ── Summary ─────────────────────────────────────────────────────── */}
      <Card title="Summary">
        <div className="kv-grid">
          <KeyVal k="Blueprint" v={blueprintLabel ?? "—"} />
          <KeyVal k="Version" v={versionLabel ?? "—"} />
          <KeyVal
            k="Plan status"
            v={<StatusIndicator tone={blocked ? "danger" : plan.status === "READY" ? "ok" : "warn"} label={plan.status} />}
          />
          <KeyVal k="Plan hash" v={shortHash(plan.planHash)} mono />
          <KeyVal k="Snapshot hash" v={shortHash(plan.snapshotHash)} mono />
          <KeyVal k="Blueprint hash" v={shortHash(plan.blueprintHash)} mono />
          <KeyVal k="Snapshot taken" v={snapshotRequestedAt ? formatAge(snapshotRequestedAt, now) : "—"} />
          <KeyVal k="Plan generated" v={generatedAt ? formatAge(generatedAt, now) : "—"} />
        </div>
        <div className="row" style={{ marginTop: "0.75rem" }}>
          <CountChip label="create" value={buckets.create} tone="ok" />
          <CountChip label="update" value={buckets.update} tone="warn" />
          <CountChip label="move" value={buckets.move} tone="neutral" />
          <CountChip label="preserve" value={buckets.preserve} tone="neutral" />
          <CountChip label="conflict" value={buckets.conflict} tone="danger" />
        </div>
        <div className="row" style={{ marginTop: "0.5rem" }}>
          <CountChip label="blockers" value={findingCounts.blocker} tone={findingCounts.blocker ? "danger" : "neutral"} />
          <CountChip label="warnings" value={findingCounts.warning} tone={findingCounts.warning ? "warn" : "neutral"} />
        </div>
        {blocked ? (
          <p className="dryrun-blocked" style={{ marginTop: "0.75rem" }}>
            This plan is <strong>blocked</strong>. Even in a future release phase, ODIN would refuse to deploy it until
            the blocker findings below are resolved.
          </p>
        ) : null}
      </Card>

      {/* ── Proposed changes ────────────────────────────────────────────── */}
      <section>
        <h2 className="section-title">
          Proposed changes <StateTag state="proposed" />
        </h2>
        <p className="section-note">
          Each row compares the <strong>current</strong> live value to the <strong>desired</strong> blueprint value.
          These are proposals only.
        </p>
        {OP_GROUP_ORDER.map((g) => {
          const ops = groups.get(g) ?? [];
          return (
            <Card key={g} title={`${OP_GROUP_LABEL[g]} (${ops.length})`}>
              {ops.length === 0 ? (
                <p className="muted">No changes proposed.</p>
              ) : (
                <div className="op-list">
                  {ops.map((op, i) => (
                    <OperationRow key={op.operationId ?? `${op.resourceKey}-${i}`} op={op} />
                  ))}
                </div>
              )}
            </Card>
          );
        })}
      </section>

      {/* ── Safety findings ─────────────────────────────────────────────── */}
      <section>
        <h2 className="section-title">Safety findings</h2>
        {(plan.findings ?? []).length === 0 ? (
          <Card>
            <p className="muted">No safety findings were raised.</p>
          </Card>
        ) : (
          SEV_ORDER.map((sev) => {
            const items = findingsBySeverity.get(sev) ?? [];
            if (items.length === 0) return null;
            return (
              <Card key={sev} title={`${SEV_LABEL[sev]} (${items.length})`}>
                <div className="finding-list">
                  {items.map((f, i) => (
                    <FindingRow key={`${f.code}-${i}`} finding={f} />
                  ))}
                </div>
              </Card>
            );
          })
        )}
      </section>

      {/* ── Persona simulation ──────────────────────────────────────────── */}
      <section>
        <h2 className="section-title">Persona simulation</h2>
        <p className="section-note">
          Who can see and do what — comparing <StateTag state="current" /> against <StateTag state="desired" />.
        </p>
        <Card>
          {(plan.personaSimulation?.comparison ?? []).length === 0 ? (
            <p className="muted">No persona comparison was produced.</p>
          ) : (
            <div className="persona-list">
              {plan.personaSimulation.comparison.map((c) => (
                <PersonaDiffRow key={c.personaId} cmp={c} chName={chName} />
              ))}
            </div>
          )}
        </Card>
      </section>

      {/* ── Freshness ───────────────────────────────────────────────────── */}
      <Card title="Freshness">
        <div className="kv-grid">
          <KeyVal k="Snapshot age" v={snapshotRequestedAt ? formatAge(snapshotRequestedAt, now) : "unknown"} />
          <KeyVal k="Plan generated" v={generatedAt ? formatAge(generatedAt, now) : "unknown"} />
          <KeyVal
            k="Plan status"
            v={<StatusIndicator tone={plan.status === "STALE" ? "warn" : "neutral"} label={plan.status} />}
          />
        </div>
        {plan.status === "STALE" ? (
          <p className="dryrun-stale" style={{ marginTop: "0.75rem" }}>
            The underlying snapshot or blueprint changed after this plan was generated. Re-run the dry-run to get a fresh
            preview.
          </p>
        ) : null}
      </Card>

      {/* ── Deploy is intentionally NOT available ───────────────────────── */}
      <div className="future-deploy" aria-label="Deploy availability">
        <span className="badge">Available in a later release phase</span>
        <p style={{ margin: "0.5rem 0 0" }}>
          Applying a blueprint to a live Discord server is not part of this release. ODIN is read-only today — {NO_CHANGES_TEXT}
        </p>
        <button type="button" className="btn btn--secondary" disabled aria-disabled="true" title="Not available in this release phase">
          Deploy (not available yet)
        </button>
      </div>

      <AdvancedJson title="Advanced — read-only plan JSON (schema context)" value={plan} />
    </div>
  );
}

function OperationRow({ op }: { op: DiffOperation }): JSX.Element {
  return (
    <div className="op-row">
      <div className="op-row__head">
        <span className="op-row__action">{op.operationType}</span>
        <code className="op-row__key">{op.resourceKey}</code>
        <SeverityBadge severity={op.severity} />
        {op.protectedResource ? <span className="marker marker--protected" title="Protected resource">protected</span> : null}
        {op.destructive ? <span className="marker marker--destructive" title="Destructive change">destructive</span> : null}
      </div>
      <p className="op-row__reason">{op.reason}</p>
      <div className="op-row__states">
        <div>
          <StateTag state="current" />
          <ValueBlock value={op.currentState} empty="not present" />
        </div>
        <span className="op-row__arrow" aria-hidden="true">→</span>
        <div>
          <StateTag state="desired" />
          <ValueBlock value={op.desiredState} empty="removed / preserved" />
        </div>
      </div>
      {(op.dependencies ?? []).length > 0 ? (
        <p className="op-row__deps">Depends on: {op.dependencies.join(", ")}</p>
      ) : null}
      <Disclosure summary="Technical detail">
        <pre className="json-view">{JSON.stringify(op, null, 2)}</pre>
      </Disclosure>
    </div>
  );
}

function ValueBlock({ value, empty }: { value: Record<string, unknown> | null; empty: string }): JSX.Element {
  if (!value || Object.keys(value).length === 0) return <span className="muted">{empty}</span>;
  const entries = Object.entries(value).slice(0, 6);
  return (
    <ul className="value-block">
      {entries.map(([k, v]) => (
        <li key={k}>
          <span className="value-block__k">{k}</span>: <span className="value-block__v">{formatVal(v)}</span>
        </li>
      ))}
    </ul>
  );
}

function formatVal(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (Array.isArray(v)) return v.length ? v.map(String).join(", ") : "[]";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function FindingRow({ finding }: { finding: SafetyFinding }): JSX.Element {
  return (
    <div className="finding-row">
      <div className="op-row__head">
        <SeverityBadge severity={finding.severity} />
        <strong>{finding.title}</strong>
        {finding.blocksDeployment ? <span className="marker marker--destructive">blocks deployment</span> : null}
      </div>
      <p className="finding-row__plain">{finding.plain}</p>
      {finding.remediation ? (
        <p className="finding-row__fix">
          <span className="finding-row__fix-label">How to fix:</span> {finding.remediation}
        </p>
      ) : null}
      {(finding.affectedResourceKeys ?? []).length > 0 ? (
        <p className="muted">Affects: {finding.affectedResourceKeys.join(", ")}</p>
      ) : null}
      <Disclosure summary="Technical detail">
        <p>
          <code>{finding.code}</code>
        </p>
        <p>{finding.technical}</p>
      </Disclosure>
    </div>
  );
}

function PersonaDiffRow({ cmp, chName }: { cmp: PersonaComparison; chName: (id: string) => string }): JSX.Element {
  return (
    <div className="persona-row">
      <div className="op-row__head">
        <strong>{cmp.label}</strong>
        <span className="muted">
          visible: {cmp.current.visibleChannels?.length ?? 0} <span aria-hidden="true">→</span>{" "}
          {cmp.desired.visibleChannels?.length ?? 0}
        </span>
        {cmp.desired.isAdministrator ? <span className="marker marker--destructive">administrator</span> : null}
      </div>
      <div className="persona-row__diffs">
        <div>
          <span className="diff-label diff-label--add">Newly visible</span>
          {(cmp.newlyVisible ?? []).length === 0 ? (
            <span className="muted"> none</span>
          ) : (
            <ul>{cmp.newlyVisible.map((id) => <li key={id}>{chName(id)}</li>)}</ul>
          )}
        </div>
        <div>
          <span className="diff-label diff-label--remove">Newly hidden</span>
          {(cmp.newlyHidden ?? []).length === 0 ? (
            <span className="muted"> none</span>
          ) : (
            <ul>{cmp.newlyHidden.map((id) => <li key={id}>{chName(id)}</li>)}</ul>
          )}
        </div>
      </div>
      {(cmp.desired.warnings ?? []).length > 0 ? (
        <p className="dryrun-stale">{cmp.desired.warnings.join(" ")}</p>
      ) : null}
    </div>
  );
}

// ── Data-fetching container ──────────────────────────────────────────────────
export function DryRun(): JSX.Element {
  const ctx = useAppContext();
  const [state, setState] = useState<
    | { kind: "idle" }
    | { kind: "loading" }
    | { kind: "list"; runs: DryRunView[] }
    | { kind: "detail"; run: DryRunView }
    | { kind: "error"; message: string }
  >({ kind: "idle" });
  const [creating, setCreating] = useState(false);
  const [versionId, setVersionId] = useState("");
  const [snapshotId, setSnapshotId] = useState("");

  const ready = ctx.tenantId && ctx.guildId;

  const loadList = () => {
    if (!ctx.tenantId || !ctx.guildId) return;
    setState({ kind: "loading" });
    api
      .listDryRuns(ctx.tenantId, ctx.guildId)
      .then((r) => setState({ kind: "list", runs: r.dryRuns }))
      .catch((e) => setState({ kind: "error", message: e instanceof ApiError ? e.message : "Could not load dry-runs" }));
  };

  useEffect(() => {
    if (ready) loadList();
    else setState({ kind: "idle" });
  }, [ctx.tenantId, ctx.guildId]); // loadList reads the same context values

  const open = (id: string) => {
    if (!ctx.tenantId || !ctx.guildId) return;
    setState({ kind: "loading" });
    api
      .getDryRun(ctx.tenantId, ctx.guildId, id)
      .then((run) => setState({ kind: "detail", run }))
      .catch((e) => setState({ kind: "error", message: e instanceof ApiError ? e.message : "Could not load dry-run" }));
  };

  const create = async () => {
    if (!ctx.tenantId || !ctx.guildId) return;
    setCreating(true);
    try {
      const run = await api.createDryRun(ctx.tenantId, ctx.guildId, {
        blueprintVersionId: versionId.trim(),
        snapshotId: snapshotId.trim(),
      });
      setState({ kind: "detail", run });
    } catch (e) {
      setState({ kind: "error", message: e instanceof ApiError ? e.message : "Could not generate dry-run" });
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <PageHeader title="Dry-run review">
        <p>
          A safe, read-only preview of what deploying a blueprint would change. {NO_CHANGES_TEXT}
        </p>
      </PageHeader>

      {!apiConfigured ? (
        <NotConfiguredState title="Control API URL not configured">
          Set <code>VITE_CONTROL_API_URL</code> to generate dry-runs.
        </NotConfiguredState>
      ) : (
        <>
          <ContextBar requireGuild />

          {!ready ? (
            <EmptyState title="Select a workspace and server">
              Choose a workspace and enter your ODIN server id above to view or generate dry-runs.
            </EmptyState>
          ) : (
            <>
              <Card title="Generate a dry-run" description="Provide a blueprint version and a completed snapshot to plan against.">
                <div className="row">
                  <input className="field__input" placeholder="Blueprint version id" value={versionId} onChange={(e) => setVersionId(e.target.value)} aria-label="Blueprint version id" />
                  <input className="field__input" placeholder="Snapshot id" value={snapshotId} onChange={(e) => setSnapshotId(e.target.value)} aria-label="Snapshot id" />
                  <Button variant="primary" disabled={creating || versionId.trim().length < 1 || snapshotId.trim().length < 1} onClick={create}>
                    {creating ? "Generating…" : "Generate dry-run"}
                  </Button>
                </div>
              </Card>

              {state.kind === "loading" ? <LoadingState label="Working…" /> : null}
              {state.kind === "error" ? <ApiUnavailableState detail={state.message} /> : null}

              {state.kind === "list" ? (
                state.runs.length === 0 ? (
                  <EmptyState title="No dry-runs yet">Generate one above to see a full preview.</EmptyState>
                ) : (
                  <Card title="Recent dry-runs">
                    <div className="op-list">
                      {state.runs.map((r) => (
                        <button key={r.id} className="list-row" onClick={() => open(r.id)}>
                          <StatusIndicator tone={r.status === "BLOCKED" ? "danger" : r.status === "READY" ? "ok" : "warn"} label={r.status} />
                          <code>{shortHash(r.planHash)}</code>
                          <span className="muted">{r.operationCount} ops · {r.blockerCount} blockers · {formatAge(r.generatedAt)}</span>
                        </button>
                      ))}
                    </div>
                  </Card>
                )
              ) : null}

              {state.kind === "detail" ? (
                <>
                  <div className="row" style={{ margin: "1rem 0" }}>
                    <Button variant="ghost" onClick={loadList}>
                      ← Back to list
                    </Button>
                  </div>
                  <DryRunReview
                    plan={state.run.content}
                    versionLabel={state.run.blueprintVersionId}
                    snapshotRequestedAt={null}
                    generatedAt={state.run.generatedAt}
                  />
                </>
              ) : null}
            </>
          )}
        </>
      )}
    </>
  );
}
